import { json } from "@remix-run/node";
import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { useOutletContext } from "@remix-run/react";
import type { Locale } from "~/utils/locale";
import { detectLocale } from "~/utils/locale";
import { t } from "~/utils/translations";
import { Breadcrumbs } from "~/components/storefront/Breadcrumbs";

export const loader = async ({ request }: LoaderFunctionArgs) => {
  return json({ locale: detectLocale(request.url) });
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const locale = data?.locale || "vi";
  return [
    { title: locale === "vi" ? "Hướng dẫn chọn size — Diolo" : "Size Guide — Diolo" },
    { name: "description", content: locale === "vi" ? "Hướng dẫn chọn size Diolo — tìm size phù hợp với bảng số đo chi tiết." : "Diolo size guide — find your perfect fit with our detailed measurement charts." },
  ];
};

export default function SizeGuidePage() {
  const { locale } = useOutletContext<{ locale: Locale }>();

  const topsSizes = [
    { size: "XS", bust: "78–82", waist: "60–64", hip: "86–90" },
    { size: "S", bust: "82–86", waist: "64–68", hip: "90–94" },
    { size: "M", bust: "86–90", waist: "68–72", hip: "94–98" },
    { size: "L", bust: "90–94", waist: "72–76", hip: "98–102" },
    { size: "XL", bust: "94–98", waist: "76–80", hip: "102–106" },
  ];

  const bottomsSizes = [
    { size: "XS", waist: "60–64", hip: "86–90", inseam: "66" },
    { size: "S", waist: "64–68", hip: "90–94", inseam: "67" },
    { size: "M", waist: "68–72", hip: "94–98", inseam: "68" },
    { size: "L", waist: "72–76", hip: "98–102", inseam: "69" },
    { size: "XL", waist: "76–80", hip: "102–106", inseam: "70" },
  ];

  return (
    <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8 py-12">
      <Breadcrumbs locale={locale} items={[{ label: t("sizeGuide", locale) }]} />
      {/* Header */}
      <div className="text-center mb-12">
        <p className="text-xs uppercase tracking-widest text-luxury-stone mb-4">
          {locale === "vi" ? "Hướng dẫn" : "Guide"}
        </p>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light text-luxury-charcoal tracking-normal">
          {locale === "vi" ? "Hướng dẫn chọn size" : "Size Guide"}
        </h1>
        <p className="mt-4 text-luxury-stone text-sm">
          {locale === "vi"
            ? "Tất cả số đo tính bằng cm"
            : "All measurements in cm"}
        </p>
      </div>

      <div className="max-w-2xl mx-auto">
        {/* How to Measure */}
        <div className="mb-12">
          <h2 className="font-display text-lg font-light text-luxury-charcoal tracking-normal mb-5">
            {locale === "vi" ? "Cách đo" : "How to Measure"}
          </h2>
          <div className="space-y-4">
            {[
              {
                label: locale === "vi" ? "Vòng ngực" : "Bust",
                desc: locale === "vi"
                  ? "Đo vòng quanh phần đầy đặn nhất của ngực, giữ thước dây ngang và thoải mái."
                  : "Measure around the fullest part of your bust, keeping the tape level and comfortable.",
              },
              {
                label: locale === "vi" ? "Vòng eo" : "Waist",
                desc: locale === "vi"
                  ? "Đo vòng quanh phần nhỏ nhất của eo tự nhiên, thường nằm trên rốn."
                  : "Measure around the narrowest part of your natural waistline, typically above the navel.",
              },
              {
                label: locale === "vi" ? "Vòng hông" : "Hip",
                desc: locale === "vi"
                  ? "Đo vòng quanh phần rộng nhất của hông, thường cách eo khoảng 20cm."
                  : "Measure around the widest part of your hips, typically about 20cm below your waist.",
              },
              {
                label: locale === "vi" ? "Chiều dài ống" : "Inseam",
                desc: locale === "vi"
                  ? "Đo từ đáy quần dọc theo bên trong chân đến mắt cá chân."
                  : "Measure from the crotch seam along the inside of the leg to the ankle.",
              },
            ].map((item) => (
              <div key={item.label} className="flex gap-4">
                <span className="flex-shrink-0 text-xs uppercase tracking-widest text-luxury-charcoal w-24 pt-0.5">
                  {item.label}
                </span>
                <p className="text-luxury-stone text-sm leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Tops Table */}
        <div className="mb-12">
          <h2 className="font-display text-lg font-light text-luxury-charcoal tracking-normal mb-5">
            {locale === "vi" ? "Áo — Bảng size" : "Tops — Size Chart"}
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-luxury-sand">
                  <th className="text-left py-3 pr-4 text-xs uppercase tracking-widest text-luxury-stone font-normal">
                    {t("size", locale)}
                  </th>
                  <th className="text-left py-3 pr-4 text-xs uppercase tracking-widest text-luxury-stone font-normal">
                    {locale === "vi" ? "Ngực (cm)" : "Bust (cm)"}
                  </th>
                  <th className="text-left py-3 pr-4 text-xs uppercase tracking-widest text-luxury-stone font-normal">
                    {t("waistCm", locale)}
                  </th>
                  <th className="text-left py-3 text-xs uppercase tracking-widest text-luxury-stone font-normal">
                    {t("hipCm", locale)}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-luxury-sand/50">
                {topsSizes.map((row) => (
                  <tr key={row.size}>
                    <td className="py-3 pr-4 font-display text-luxury-charcoal tracking-normal">
                      {row.size}
                    </td>
                    <td className="py-3 pr-4 text-luxury-stone">{row.bust}</td>
                    <td className="py-3 pr-4 text-luxury-stone">{row.waist}</td>
                    <td className="py-3 text-luxury-stone">{row.hip}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Bottoms Table */}
        <div className="mb-12">
          <h2 className="font-display text-lg font-light text-luxury-charcoal tracking-normal mb-5">
            {locale === "vi" ? "Quần — Bảng size" : "Bottoms — Size Chart"}
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-luxury-sand">
                  <th className="text-left py-3 pr-4 text-xs uppercase tracking-widest text-luxury-stone font-normal">
                    {t("size", locale)}
                  </th>
                  <th className="text-left py-3 pr-4 text-xs uppercase tracking-widest text-luxury-stone font-normal">
                    {t("waistCm", locale)}
                  </th>
                  <th className="text-left py-3 pr-4 text-xs uppercase tracking-widest text-luxury-stone font-normal">
                    {t("hipCm", locale)}
                  </th>
                  <th className="text-left py-3 text-xs uppercase tracking-widest text-luxury-stone font-normal">
                    {locale === "vi" ? "Dài ống (cm)" : "Inseam (cm)"}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-luxury-sand/50">
                {bottomsSizes.map((row) => (
                  <tr key={row.size}>
                    <td className="py-3 pr-4 font-display text-luxury-charcoal tracking-normal">
                      {row.size}
                    </td>
                    <td className="py-3 pr-4 text-luxury-stone">{row.waist}</td>
                    <td className="py-3 pr-4 text-luxury-stone">{row.hip}</td>
                    <td className="py-3 text-luxury-stone">{row.inseam}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Tip */}
        <div className="bg-luxury-cream p-6 rounded-sm">
          <p className="text-xs uppercase tracking-widest text-luxury-charcoal mb-2">
            {locale === "vi" ? "Mẹo chọn size" : "Sizing Tip"}
          </p>
          <p className="text-luxury-stone text-sm leading-relaxed">
            {locale === "vi"
              ? "Nếu bạn ở giữa hai size, chúng tôi khuyên bạn nên chọn size lớn hơn để thoải mái nhất. Các sản phẩm Diolo được thiết kế ôm nhẹ cơ thể nhưng không gò bó."
              : "If you're between two sizes, we recommend sizing up for the most comfortable fit. Diolo products are designed to fit close to the body without feeling restrictive."}
          </p>
        </div>
      </div>
    </div>
  );
}

export { RouteErrorBoundary as ErrorBoundary } from "~/components/RouteErrorBoundary";
